import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ProductSelect } from '@/components/selects';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';

export type ProductLineDraft = {
  id: string;
  productId: string;
  quantity: string;
  unitPrice?: string;
  unitPriceEstimate?: string;
};

let lineSeq = 0;

export function createProductLineDraft(partial?: Partial<Omit<ProductLineDraft, 'id'>>): ProductLineDraft {
  lineSeq += 1;
  return {
    id: `line-${lineSeq}-${Date.now()}`,
    productId: '',
    quantity: '1',
    ...partial,
  };
}

export function parseProductLines(lines: ProductLineDraft[], mode: 'basic'): Array<{ productId: number; quantity: number }>;
export function parseProductLines(
  lines: ProductLineDraft[],
  mode: 'estimate',
): Array<{ productId: number; quantity: number; unitPriceEstimate?: number }>;
export function parseProductLines(
  lines: ProductLineDraft[],
  mode: 'price',
): Array<{ productId: number; quantity: number; unitPrice: number }>;
export function parseProductLines(
  lines: ProductLineDraft[],
  mode: 'basic' | 'estimate' | 'price' = 'basic',
) {
  return lines
    .filter((line) => line.productId && Number(line.quantity) > 0)
    .map((line) => {
      if (mode === 'price') {
        return {
          productId: Number(line.productId),
          quantity: Number(line.quantity),
          unitPrice: line.unitPrice != null && line.unitPrice !== '' ? Number(line.unitPrice) : 0,
        };
      }
      if (mode === 'estimate') {
        const item: { productId: number; quantity: number; unitPriceEstimate?: number } = {
          productId: Number(line.productId),
          quantity: Number(line.quantity),
        };
        if (line.unitPriceEstimate != null && line.unitPriceEstimate !== '') {
          item.unitPriceEstimate = Number(line.unitPriceEstimate);
        }
        return item;
      }
      return {
        productId: Number(line.productId),
        quantity: Number(line.quantity),
      };
    });
}

interface ProductLineItemsEditorProps {
  lines: ProductLineDraft[];
  onChange: (lines: ProductLineDraft[]) => void;
  /** Show unit price column for vendor bills */
  priceMode?: 'none' | 'estimate' | 'price';
  className?: string;
  minLines?: number;
}

export function ProductLineItemsEditor({
  lines,
  onChange,
  priceMode = 'none',
  className,
  minLines = 1,
}: ProductLineItemsEditorProps) {
  const updateLine = (id: string, patch: Partial<ProductLineDraft>) => {
    onChange(lines.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const removeLine = (id: string) => {
    if (lines.length <= minLines) return;
    onChange(lines.filter((line) => line.id !== id));
  };

  const addLine = () => {
    onChange([...lines, createProductLineDraft()]);
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
          Line items <span className="text-red-600">*</span>
        </p>
        <Button type="button" variant="secondary" className="!px-2 !py-1 text-xs" onClick={addLine}>
          <AddIcon sx={{ fontSize: 16 }} /> Add line
        </Button>
      </div>
      <div className="space-y-2">
        {lines.map((line, index) => (
          <div
            key={line.id}
            className="grid gap-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700 md:grid-cols-12"
          >
            <div className="md:col-span-5">
              <ProductSelect
                label={index === 0 ? 'Product' : undefined}
                value={line.productId}
                onChange={(productId) => updateLine(line.id, { productId })}
                required
              />
            </div>
            <div className="md:col-span-2">
              <Input
                label={index === 0 ? 'Quantity' : undefined}
                type="number"
                min={0.001}
                step="any"
                value={line.quantity}
                onChange={(e) => updateLine(line.id, { quantity: e.target.value })}
                required
              />
            </div>
            {priceMode === 'estimate' && (
              <div className="md:col-span-3">
                <Input
                  label={index === 0 ? 'Est. unit price' : undefined}
                  type="number"
                  min={0}
                  step="any"
                  value={line.unitPriceEstimate ?? ''}
                  onChange={(e) => updateLine(line.id, { unitPriceEstimate: e.target.value })}
                />
              </div>
            )}
            {priceMode === 'price' && (
              <div className="md:col-span-3">
                <Input
                  label={index === 0 ? 'Unit price' : undefined}
                  type="number"
                  min={0}
                  step="any"
                  value={line.unitPrice ?? ''}
                  onChange={(e) => updateLine(line.id, { unitPrice: e.target.value })}
                  required
                />
              </div>
            )}
            <div className="flex items-end md:col-span-1">
              <Button
                type="button"
                variant="ghost"
                className="!px-2"
                disabled={lines.length <= minLines}
                onClick={() => removeLine(line.id)}
                aria-label="Remove line"
              >
                <DeleteOutlineIcon fontSize="small" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
